import type {
  BulkRequestInput,
  CloneTicket,
  ExportRequestInput,
  Job,
  JobAccepted,
  MoveResult,
  MoveTicket,
  Ticket,
} from '@servicetim/shared';
import { useMutation } from '@tanstack/react-query';
import { portalPath, useApiContext } from './client';

const ticketsBase = portalPath('/tickets');

/** `POST /v1/tickets/{id}/move`: other project/issue type; reports unmapped custom fields. */
export function useMoveTicket() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: Partial<MoveTicket> & { id: string; projectId: string; issueTypeId: string }) =>
      request<MoveResult>('POST', `${ticketsBase}/${id}/move`, {
        body: { ...input, fieldMapping: input.fieldMapping ?? {} },
      }),
    onSuccess: (result, { id }) =>
      invalidate(
        keys.ticket(id).scope,
        keys.ticket(result.ticket.key).scope,
        keys.search.all,
        keys.views.all,
        keys.jobs.all,
      ),
  });
}

/** `POST /v1/tickets/{id}/clone` (201). */
export function useCloneTicket() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<CloneTicket> & { id: string }) =>
      request<Ticket>('POST', `${ticketsBase}/${id}/clone`, {
        body: { projectId: input.projectId, includeComments: input.includeComments ?? false },
      }),
    onSuccess: (_t, { id }) =>
      invalidate(keys.ticket(id).links, keys.ticket(id).history, keys.search.all, keys.views.all),
  });
}

/**
 * `POST /v1/tickets/bulk`: ≤ 100 targets run inline (200 with the finished job), more are
 * queued (202 `{ jobId }`). Poll with `useJob(jobId)`.
 */
export function useBulkTickets() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: BulkRequestInput) =>
      request<Job | JobAccepted>('POST', `${ticketsBase}/bulk`, { body: input }),
    onSuccess: () => invalidate(keys.tickets.all, keys.search.all, keys.views.all, keys.jobs.all),
  });
}

/** `POST /v1/exports` (202 `{ jobId }`); the finished job's progress carries the download URL. */
export function useCreateExport() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: ExportRequestInput) =>
      request<JobAccepted>('POST', portalPath('/exports'), { body: input }),
    onSuccess: () => invalidate(keys.jobs.all),
  });
}

/** Narrows the bulk response: `jobId` only (queued) vs. a finished `Job`. */
export const isJobAccepted = (value: Job | JobAccepted): value is JobAccepted =>
  !('status' in value);
